import { Injectable, signal } from '@angular/core';
import type { Organization, User } from '../api/models';
import { AppRepository } from '../api/repository';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  readonly profile = signal<Organization | null>(null);
  readonly logoUrl = signal<string | null>(null);

  private objectUrl: string | null = null;

  constructor(private readonly repository: AppRepository) {}

  /** Organization admin still has to save the organization profile. */
  needsSetup(user: User | null | undefined, org: Organization | null = this.profile()): boolean {
    return user?.is_org_admin === true && org?.initialized === false;
  }

  postAuthPath(user: User | null | undefined): '/members' | '/projects' {
    return this.needsSetup(user) ? '/members' : '/projects';
  }

  async load(): Promise<Organization | null> {
    try {
      const org = await this.repository.getOrganization();
      this.profile.set(org);
      await this.refreshLogo(org);
      return org;
    } catch {
      return this.profile();
    }
  }

  private async refreshLogo(org: Organization): Promise<void> {
    this.clearLogo();
    if (!org.has_logo) return;
    try {
      const blob = await this.repository.organizationLogo();
      this.objectUrl = URL.createObjectURL(blob);
      this.logoUrl.set(this.objectUrl);
    } catch {
      this.logoUrl.set(null);
    }
  }

  private clearLogo(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.logoUrl.set(null);
  }
}
